import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError, isAxiosError } from 'axios';
import { authApiClient } from '../../../api/axios';
import { showSnackbar } from '../../../api/snackbarSlice';

export const fetchCurrentApiVersion = async () => {
  const { data } = await authApiClient.get('/organization/settings');
  return { active_api_version: data.active_api_version };
};

export const updateApiVersion = async ({ active_api_version }) => {
  const { data } = await authApiClient.patch('/organization/settings/active-api-version', {
    active_api_version,
  });
  return data;
};

export const useGetApiVersionSettingsHandler = () => {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: ['organization-active-api-version'],
    queryFn: fetchCurrentApiVersion,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (!query.isError) return;

    if (isAxiosError(query.error)) {
      dispatch(
        showSnackbar({
          message: query.error.response?.data?.errors?.message || query.error.message,
          severity: 'error',
        }),
      );
      return;
    }

    dispatch(
      showSnackbar({
        message: 'Failed to fetch API version settings',
        severity: 'error',
      }),
    );
  }, [dispatch, query.error, query.isError]);

  return {
    settings: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const useUpdateApiVersionHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateApiVersion,
    onSuccess: (data, variables) => {
      const active_api_version = data?.active_api_version ?? variables.active_api_version;
      queryClient.setQueryData(['organization-active-api-version'], { active_api_version });
      dispatch(
        showSnackbar({
          message: `API version updated to ${active_api_version}`,
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(data, variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
        return;
      }

      dispatch(
        showSnackbar({
          message: 'Failed to update API version',
          severity: 'error',
        }),
      );
    },
  });

  const onSubmit = async (active_api_version) => {
    mutation.mutate({ active_api_version });
  };

  return {
    onSubmit,
    isPending: mutation.isPending,
  };
};
